import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { AUTH_CONFIG, type AuthConfig } from '@olv/ui-shared/features/auth';
import {
  Account,
  CreateAccountDto,
  UpdateAccountDto,
  AccountFilters,
  PagedResult,
  AccountStatus,
  CreateAccountResponse,
} from '../models/account.interface';
import { PagedResultDto } from '../models/paged-result.dto';
import { UserDto, userDtoToAccount } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class MerchantAccountService {
  private http = inject(HttpClient);

  private readonly cfg = inject<AuthConfig>(AUTH_CONFIG);
  private readonly baseUrl = `${this.cfg.baseUrl}/api/MerchantAccount`;

  // Signal-based state management
  private accountsSignal = signal<Account[]>([]);
  private selectedAccountSignal = signal<Account | null>(null);
  private loadingSignal = signal<boolean>(false);
  private filtersSignal = signal<AccountFilters>({});
  private totalCountSignal = signal<number>(0);
  private updateCounter = signal(0);
  private accountGroupsSignal = signal<{ id: string; name: string }[]>([]);

  // Computed signals
  readonly accounts = this.accountsSignal.asReadonly();
  readonly selectedAccount = this.selectedAccountSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly filters = this.filtersSignal.asReadonly();
  readonly totalCount = this.totalCountSignal.asReadonly();
  readonly profileUpdated = computed(() => this.updateCounter());
  readonly filteredAccounts = computed(() => this.accountsSignal());

  getAccounts(merchantId: string, filters?: AccountFilters, pageIndex = 0): Observable<PagedResult<Account>> {
    this.loadingSignal.set(true);

    let params = new HttpParams();

    // Backend-supported filters
    if (filters?.search) {
      params = params.set('Filter', filters.search);
    }

    if (filters?.status) {
      const isActive = filters.status === AccountStatus.ACTIVE;
      params = params.set('IsActive', isActive.toString());
    }

    if (filters?.role !== undefined) {
      params = params.set('Role', filters.role.toString());
    }

    // Sorting
    if (filters?.sortBy) {
      let sorting = '';
      switch (filters.sortBy) {
        case 'name_asc':
          sorting = 'name ASC';
          break;
        case 'name_desc':
          sorting = 'name DESC';
          break;
      }
      params = params.set('Sorting', sorting);
    }

    params = params.set('SkipCount', pageIndex.toString());

    return this.http.get<PagedResultDto<UserDto>>(`${this.baseUrl}/${merchantId}/users`, { params }).pipe(
      map((response) => {
        const accounts = response.items.map(userDtoToAccount);
        return {
          items: accounts,
          totalCount: response.totalCount,
          pageIndex,
          totalPages: Math.ceil(response.totalCount),
        };
      }),
      tap((result) => {
        this.accountsSignal.set(result.items);
        this.totalCountSignal.set(result.totalCount || 0);
      }),
      catchError((error) => {
        console.error('Error loading merchant accounts:', error);
        return throwError(() => new Error('Failed to load merchant accounts'));
      }),
      finalize(() => {
        this.loadingSignal.set(false);
      }),
    );
  }

  getAccount(merchantId: string, id: string): Observable<Account> {
    return this.http.get<UserDto>(`${this.baseUrl}/${merchantId}/users/${id}`).pipe(
      map((user) => userDtoToAccount(user)),
      tap((account) => this.selectedAccountSignal.set(account)),
      catchError((error) => {
        console.error('Error loading merchant account:', error);
        return throwError(() => new Error(`Failed to load merchant account: ${error.message}`));
      }),
    );
  }

  createAccount(merchantId: string, accountData: CreateAccountDto): Observable<CreateAccountResponse> {
    this.loadingSignal.set(true);

    const formData = new FormData();

    formData.append('FirstName', accountData.firstName);
    formData.append('LastName', accountData.lastName);
    formData.append('CompanyPosition', accountData.position);
    formData.append('Email', accountData.email);

    // Append group IDs
    if (Array.isArray(accountData.groups)) {
      accountData.groups.forEach((groupId) => {
        formData.append('Groups', groupId);
      });
    }

    // Append profile file if available
    if (accountData.profileFile instanceof File) {
      formData.append('ProfileFile', accountData.profileFile);
    }

    return this.http.post<CreateAccountResponse>(`${this.baseUrl}/${merchantId}/users`, formData).pipe(
      catchError((error) => {
        const serverMessage = error.error || error.message || 'Unknown error';
        return throwError(() => new Error(serverMessage));
      }),
      finalize(() => {
        this.loadingSignal.set(false);
      }),
    );
  }

  updateAccount(merchantId: string, id: string, accountData: UpdateAccountDto): Observable<Account> {
    const formData = new FormData();

    if (accountData.firstName != null) {
      formData.append('FirstName', accountData.firstName);
    }
    if (accountData.lastName != null) {
      formData.append('LastName', accountData.lastName);
    }
    if (accountData.position != null) {
      formData.append('CompanyPosition', accountData.position);
    }
    if (accountData.groups != null) {
      accountData.groups.forEach((groupId) => {
        formData.append('Groups', groupId);
      });
    }
    if (accountData.profileFile != null) {
      formData.append('ProfileFile', accountData.profileFile);
    }

    return this.http.put<UserDto>(`${this.baseUrl}/${merchantId}/users/${id}`, formData).pipe(
      map(userDtoToAccount),
      tap(() => {
        this.updateCounter.update((n) => n + 1);
      }),
      catchError((error) => {
        console.error('Error updating merchant account:', error);
        return throwError(() => new Error(`Failed to update merchant account: ${error.message}`));
      }),
    );
  }

  deactivateAccount(merchantId: string, id: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${merchantId}/users/${id}/disable`, {}).pipe(
      tap(() => {
        // Update local state
        const currentAccounts = this.accountsSignal();
        const accountIndex = currentAccounts.findIndex((acc) => acc.id === id);
        if (accountIndex >= 0) {
          const updatedAccounts = [...currentAccounts];
          updatedAccounts[accountIndex] = {
            ...updatedAccounts[accountIndex],
            status: AccountStatus.INACTIVE,
          };
          this.accountsSignal.set(updatedAccounts);
        }
      }),
      catchError((error) => {
        console.error('Error deactivating merchant account:', error);
        return throwError(() => new Error(`Failed to deactivate merchant account: ${error.message}`));
      }),
    );
  }

  activateAccount(merchantId: string, id: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${merchantId}/users/${id}/enable`, {}).pipe(
      tap(() => {
        // Update local state
        const currentAccounts = this.accountsSignal();
        const accountIndex = currentAccounts.findIndex((acc) => acc.id === id);
        if (accountIndex >= 0) {
          const updatedAccounts = [...currentAccounts];
          updatedAccounts[accountIndex] = {
            ...updatedAccounts[accountIndex],
            status: AccountStatus.ACTIVE,
          };
          this.accountsSignal.set(updatedAccounts);
        }
      }),
      catchError((error) => {
        console.error('Error activating merchant account:', error);
        return throwError(() => new Error(`Failed to activate merchant account: ${error.message}`));
      }),
    );
  }

  deleteAccount(merchantId: string, id: string): Observable<void> {
    // Note: Backend doesn't have a delete endpoint, only disable
    return this.deactivateAccount(merchantId, id);
  }

  // Security actions
  resetPassword(merchantId: string, accountId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${merchantId}/users/${accountId}/reset-password`, {}).pipe(
      tap(() => {
        console.log(`Account reset initiated for user ${accountId}`);
      }),
      catchError((error) => {
        console.error('Error resetting account:', error);
        return throwError(() => new Error(`Failed to reset account: ${error.message}`));
      }),
    );
  }

  reset2FA(merchantId: string, accountId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${merchantId}/users/${accountId}/reset-2fa`, {}).pipe(
      tap(() => {
        console.log(`2FA reset initiated for user ${accountId}`);
      }),
      catchError((error) => {
        console.error('Error resetting 2FA:', error);
        return throwError(() => new Error(`Failed to reset 2FA: ${error.message}`));
      }),
    );
  }

  resetAccount(merchantId: string, accountId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/${merchantId}/users/${accountId}/reset-account`, {}).pipe(
      tap(() => {
        console.log(`Account reset initiated for user ${accountId}`);
      }),
      catchError((error) => {
        console.error('Error resetting account:', error);
        return throwError(() => new Error(`Failed to reset account: ${error.message}`));
      }),
    );
  }

  // Call to get all account groups
  getAccountGroups(merchantId: string): Observable<{ id: string; name: string }[]> {
    const cached = this.accountGroupsSignal();
    if (cached.length > 0) {
      return of(cached);
    }
    return this.http.get<{ id: string; name: string }[]>(`${this.baseUrl}/${merchantId}/users/groups`).pipe(
      tap((groups) => this.accountGroupsSignal.set(groups)),
      catchError((error) => {
        console.error('Error fetching merchant account groups:', error);
        return throwError(() => new Error('Failed to load merchant account groups'));
      }),
    );
  }

  // State management methods
  setFilters(filters: AccountFilters): void {
    this.filtersSignal.set({ ...this.filtersSignal(), ...filters });
  }

  clearFilters(): void {
    this.filtersSignal.set({});
  }

  selectAccount(account: Account | null): void {
    this.selectedAccountSignal.set(account);
  }
}
